export const ERROR_MESSAGES = {
  network: 'Netzwerkfehler.',
  request: 'Anfrage fehlgeschlagen.',
  register: 'Registrierung fehlgeschlagen.',
  login: 'Anmeldung fehlgeschlagen.',
  twoFactor: '2FA-Überprüfung fehlgeschlagen.'
} as const;

export type ErrorMessageKey = keyof typeof ERROR_MESSAGES;

export class ApiError extends Error {
  constructor(
    message: string,
    public status?: number,
    public details?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export async function requestJson<T>(
  url: string,
  options: RequestInit = {},
  fallbackMessage: string = ERROR_MESSAGES.request,
  timeoutMs = 10000
): Promise<T> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  const onCallerAbort = () => controller.abort();
  if (options.signal) {
    if (options.signal.aborted) {
      controller.abort();
    } else {
      options.signal.addEventListener('abort', onCallerAbort);
    }
  }

  let response: Response;
  try {
    response = await fetch(url, {
      ...options,
      credentials: options.credentials ?? 'same-origin',
      signal: controller.signal,
    });
  } catch (err) {
    if ((err as Error).name === 'AbortError') {
      if (options.signal?.aborted) {
        throw new ApiError('Anfrage abgebrochen.');
      }
      console.error('Request timed out');
      throw new ApiError('Anfragezeit überschritten.');
    }
    console.error('Network error while fetching');
    throw new ApiError(ERROR_MESSAGES.network);
  } finally {
    clearTimeout(timer);
    if (options.signal) {
      options.signal.removeEventListener('abort', onCallerAbort);
    }
  }

  const statusWithoutBody = response.status === 204 || response.status === 205 || response.status === 304;
  const mayContainJsonBody = !statusWithoutBody;

  let data: unknown = {};
  if (mayContainJsonBody) {
    try {
      data = await response.json();
    } catch {
      console.error('Failed to parse JSON response');
      if (!response.ok) {
        throw new ApiError(fallbackMessage, response.status);
      }
    }
  }

  if (!response.ok) {
    const message = (data as { error?: string })?.error || fallbackMessage;
    throw new ApiError(message, response.status, data);
  }

  return data as T;
}
